import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { api, shortDate } from "../lib/api";
import { PageHeader, Panel, NeonButton } from "../components/common";

const PROCS = ["stripe", "adyen", "paypal", "square"];
const STATUS = { pending: "#FFB800", processing: "#37E5D7", done: "#CCFF00", dead_letter: "#F87171" };
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks`;
const SIG = { stripe: "STRIPE_WEBHOOK_SECRET", adyen: "ADYEN_HMAC_KEY", paypal: "verification not enforced", square: "SQUARE_SIGNATURE_KEY" };

const Stat = ({ k, v, c }) => (
  <div className="p-5 border-r border-b border-line" data-testid={`queue-stat-${k}`}>
    <p className="label-xs">{k.replace("_", " ")}</p>
    <p className="font-mono text-3xl font-extrabold mt-2 tabular" style={{ color: c }}>{v ?? "—"}</p>
  </div>
);

export default function Ingestion() {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("");
  const load = useCallback(async () => {
    const [s, j] = await Promise.all([api.get("/queue/stats"), api.get(`/queue/jobs${filter ? `?status=${filter}` : ""}`)]);
    setStats(s.data);
    setJobs(j.data);
  }, [filter]);
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);

  const retry = async (id) => { await api.post(`/queue/jobs/${id}/retry`); toast.success("Re-queued"); load(); };

  return (
    <div data-testid="ingestion-page">
      <PageHeader eyebrow="// unified processor ingestion" title="Webhook Ingestion">
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-5 border-l border-t border-line bg-surface">
        <Stat k="pending" v={stats?.pending} c={STATUS.pending} />
        <Stat k="processing" v={stats?.processing} c={STATUS.processing} />
        <Stat k="done" v={stats?.done} c={STATUS.done} />
        <Stat k="dead_letter" v={stats?.dead_letter} c={STATUS.dead_letter} />
        <Stat k="avg_latency_ms" v={stats?.avg_latency_ms} c="#FFFFFF" />
      </div>
      <div className="grid grid-cols-12 gap-6 mt-6">
        <Panel title="Queue jobs · retry with exponential backoff · dead-letter after 5" className="col-span-12 xl:col-span-8"
          action={<select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-canvas border border-white/15 font-mono text-[11px] px-2 py-1" data-testid="queue-filter">
            <option value="">all</option>{Object.keys(STATUS).map((s) => <option key={s}>{s}</option>)}</select>}>
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[640px]" data-testid="queue-table">
              <thead><tr className="label-xs">{["Job", "Processor", "Status", "Attempts", "Received", "Latency", "Result / error", ""].map((h) => <th key={h} className="pb-3 font-normal pr-3">{h}</th>)}</tr></thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-b border-divider font-mono text-[11px]" data-testid={`queue-row-${j.id}`}>
                    <td className="py-2.5 pr-3 text-white/70">{j.id}</td>
                    <td className="pr-3 uppercase">{j.processor}</td>
                    <td className="pr-3" style={{ color: STATUS[j.status] }}>{j.status}</td>
                    <td className="pr-3">{j.attempts}</td>
                    <td className="pr-3 text-white/45">{shortDate(j.received_at)}</td>
                    <td className="pr-3">{j.latency_ms ? `${j.latency_ms} ms` : "—"}</td>
                    <td className="pr-3 text-white/55 max-w-[220px] truncate">{j.last_error || (j.result?.dispute_ids || []).join(", ") || "—"}</td>
                    <td>{j.status === "dead_letter" && <button onClick={() => retry(j.id)} className="text-white/50 hover:text-brand" data-testid={`retry-${j.id}`}><RotateCcw className="w-3.5 h-3.5" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
        <Panel title="Processor endpoints" className="col-span-12 xl:col-span-4 self-start">
          <ul className="space-y-4" data-testid="processor-endpoints">
            {PROCS.map((p) => (
              <li key={p}>
                <p className="font-mono text-xs uppercase text-brand">{p} <span className="text-white/35 normal-case">· {stats?.by_processor?.[p] || 0} jobs</span></p>
                <code className="block text-[10px] text-white/60 break-all mt-1">{BASE}/{p}</code>
                <p className="text-[10px] text-white/35 mt-0.5">signature: {SIG[p]}</p>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
